import { requireCompletedProfile } from "@hisani/auth";
import { IdSchema } from "@hisani/core";
import { z } from "zod";
import { BreedSchema, PLANNER_ERROR_CODES, PlannerSortSchema, SurfaceSchema, } from "./contracts.js";
import { plannerError } from "./errors.js";
const browseControlsSchema = z.object({
    search: z.string().trim().min(1).max(120).optional(),
    sort: PlannerSortSchema.default("NEWEST"),
});
const querySchema = z
    .object({
    ...browseControlsSchema.shape,
    jurisdiction: z.string().trim().min(1).max(20).optional(),
    racecourse: z.string().trim().min(1).max(20).optional(),
    dateFrom: z.string().date().optional(),
    dateTo: z.string().date().optional(),
    surface: SurfaceSchema.optional(),
    distanceFrom: z.coerce.number().int().min(100).max(10_000).optional(),
    distanceTo: z.coerce.number().int().min(100).max(10_000).optional(),
    grade: z.string().trim().min(1).max(120).optional(),
    category: z.string().trim().min(1).max(120).optional(),
    ageRestriction: z.string().trim().min(1).max(80).optional(),
    sexRestriction: z.string().trim().min(1).max(120).optional(),
    handicapRating: z.coerce.number().int().min(0).max(200).optional(),
    benchmarkRating: z.coerce.number().int().min(0).max(200).optional(),
    ratedMinimum: z.coerce.number().int().min(0).max(200).optional(),
    uaeBredOnly: z
        .literal("true")
        .transform(() => true)
        .optional(),
    privateOwnersOnly: z
        .literal("true")
        .transform(() => true)
        .optional(),
    invitationOnly: z
        .literal("true")
        .transform(() => true)
        .optional(),
    breed: BreedSchema.optional(),
    season: z.string().trim().min(1).max(80).optional(),
    cursor: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}_[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i)
        .optional(),
    limit: z.coerce.number().int().min(1).max(100).default(30),
})
    .refine((value) => value.dateFrom === undefined ||
    value.dateTo === undefined ||
    value.dateFrom <= value.dateTo, { message: "The end date must not be before the start date." })
    .refine((value) => value.distanceFrom === undefined ||
    value.distanceTo === undefined ||
    value.distanceFrom <= value.distanceTo, { message: "The maximum distance must not be below the minimum." })
    .refine((value) => value.racecourse === undefined || value.jurisdiction !== undefined, {
    message: "A jurisdiction is required when filtering by racecourse.",
    path: ["racecourse"],
});
export async function browseRaces(input, principal, dependencies) {
    const parsed = querySchema.parse(input);
    return dependencies.repository.listPublished(parsed, principal?.account.id ?? null);
}
export async function getPlannerPlaces(dependencies) {
    return dependencies.repository.listPlaces();
}
export async function getSavedRaces(principal, input, dependencies) {
    const authenticated = requireCompletedProfile(principal);
    return dependencies.repository.listSaved(authenticated.account.id, browseControlsSchema.parse(input));
}
export async function changeSavedRace(principal, input, dependencies) {
    const authenticated = requireCompletedProfile(principal);
    const found = await dependencies.repository.setSaved({
        accountId: authenticated.account.id,
        raceId: IdSchema.parse(input.raceId),
        saved: input.saved,
        now: dependencies.clock.now(),
    });
    if (!found)
        throw plannerError(PLANNER_ERROR_CODES.notFound, "Race not found.");
    return { saved: input.saved };
}
